import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChatBox } from './ChatBox'
import { IntroSection } from './IntroSection'
import { getPersonalInfo } from '../../../config/configLoader'
import contentService from '../../../services/contentService'
import { useTheme } from '../../../contexts/ThemeContext'
import { ParticleField, AuroraBackground, PerspectiveGrid, OrbitDots, Scanline } from '../../ui/FloatingElement'

export const HomeSection = () => {
  const personalInfo = getPersonalInfo()
  const { isDark } = useTheme()
  const navigate = useNavigate()

  const [hero, setHero] = useState({
    title: personalInfo.name,
    subtitle: personalInfo.title,
    description: personalInfo.subtitle,
  })

  useEffect(() => {
    contentService.getByType('hero')
      .then(({ data }) => {
        if (data) setHero(prev => ({ ...prev, ...data }))
      })
      .catch(() => {})
  }, [])

  return (
    <div className="min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center py-16 relative">
      {/* === Full-viewport background effects (break out of max-w-6xl container) === */}
      <div className="absolute inset-0 pointer-events-none -z-10" style={{
        left: '50%',
        width: '100vw',
        transform: 'translateX(-50%)',
        overflow: 'hidden',
      }}>
        <AuroraBackground />
        <PerspectiveGrid className="opacity-60" />
        <ParticleField count={40} className="opacity-50" />
        <Scanline />
      </div>

      {/* Hero block */}
      <motion.div
        initial={{ opacity: 0, y: 40, filter: 'blur(12px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="text-center mb-14 max-w-2xl mx-auto px-4 relative z-10"
      >
        {/* Profile image with orbiting dots + animated ring */}
        <motion.div 
          className="mb-8 relative inline-block"
          initial={{ scale: 0.4, opacity: 0, rotate: -15 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.8, type: 'spring', stiffness: 180, damping: 18 }}
        >
          {/* Outer pulse ring */}
          <div className="absolute -inset-4 rounded-full animate-pulse-ring"
            style={{
              border: '1px solid',
              borderColor: isDark ? 'rgba(20,184,166,0.2)' : 'rgba(20,184,166,0.15)',
            }}
          />
          {/* Spinning gradient ring */}
          <div className="absolute -inset-1.5 rounded-full animate-ring-spin"
            style={{
              background: 'conic-gradient(from 0deg, transparent 0%, rgba(20,184,166,0.7) 25%, transparent 50%, rgba(139,92,246,0.4) 75%, transparent 100%)',
            }}
          />
          {/* Orbiting dots around avatar */}
          <OrbitDots count={3} radius={70} />
          {/* Image */}
          <div className={`relative w-28 h-28 mx-auto rounded-full overflow-hidden border-2 ${
            isDark ? 'border-neutral-800' : 'border-neutral-200'
          } animate-glow-pulse`}>
            <img 
              src="/profile.jpg" 
              alt={hero.title}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Name — gradient text with holographic shimmer */}
        <motion.h1
          initial={{ opacity: 0, y: 25, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.25, duration: 0.7, type: 'spring' }}
          className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 animate-gradient-text"
          style={{
            backgroundImage: isDark 
              ? 'linear-gradient(135deg, #e5e5e5, #14b8a6, #38bdf8, #5eead4, #e5e5e5)'
              : 'linear-gradient(135deg, #171717, #0d9488, #0284c7, #14b8a6, #171717)',
            backgroundSize: '300% 300%',
          }}
        >
          {hero.title}
        </motion.h1>
        
        {/* Subtitle — accent colored with cyber-flicker */}
        <motion.p
          initial={{ opacity: 0, x: -20, filter: 'blur(4px)' }}
          animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-accent-500 font-medium text-lg mb-3 animate-cyber-flicker"
        >
          {hero.subtitle}
        </motion.p>
        
        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          className={`text-sm sm:text-base max-w-lg mx-auto leading-relaxed ${
            isDark ? 'text-neutral-400' : 'text-neutral-600'
          }`}
        >
          {hero.description}
        </motion.p>

        {/* CTA button with gradient border */}
        {hero.ctaText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="mt-8"
          >
            <button
              onClick={() => navigate(hero.ctaLink || '/projects')}
              className={`
                inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium
                transition-all duration-300 border-gradient cursor-pointer
                ${isDark
                  ? 'bg-neutral-900/80 text-accent-400 hover:bg-accent-500/10 hover:shadow-lg hover:shadow-accent-500/20'
                  : 'bg-white/80 text-accent-600 hover:bg-accent-50 hover:shadow-lg hover:shadow-accent-500/15'
                }
              `}
            >
              {hero.ctaText}
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                →
              </motion.span>
            </button>
          </motion.div>
        )}

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-10"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className={`w-5 h-8 rounded-full border-2 mx-auto flex justify-center pt-1.5 ${
              isDark ? 'border-neutral-700' : 'border-neutral-300'
            }`}
          >
            <motion.div
              animate={{ opacity: [0.3, 1, 0.3], y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1 h-1.5 rounded-full bg-accent-500"
            />
          </motion.div>
        </motion.div>
      </motion.div>
      
      <IntroSection />
      <ChatBox />
    </div>
  )
}